import { LoadingContext, ModalContext } from "../context/contextProvider";
import { ScheduledOrderColumnsEnum, ThemeColorsEnum } from "../constants/enums";

import { AppRoutes } from "../constants/appRoutes";
import ChatIcon from "@mui/icons-material/Chat";
import CircleButton from "../components/buttons/CircleButton";
import DropdownSelect from "../components/forms/DropdownSelect";
import Services from "../services/services";
import { useContext } from "react";
import useCurrentUserInfo from "./useCurrentUserInfo";
import { useNavigate } from "react-router-dom";

const useSchedulesDataTable = (onDeleted = () => {}) => {
  const { setLoading } = useContext(LoadingContext);
  const { showConfirmation } = useContext(ModalContext);

  const navigate = useNavigate();
  const userInfo = useCurrentUserInfo();

  const handleDelete = async (workOrder) => {
    setLoading(true);
    const response = await Services.api.workOrders.delete(workOrder.id);
    setLoading(false);

    if (!response.ok)
      return Services.utility.toast(
        "Could not delete the schedule",
        ThemeColorsEnum.error
      );

    Services.utility.toast("Schedule deleted", ThemeColorsEnum.success);
    onDeleted(workOrder);
  };

  const handleShowNotes = (workOrder, event) => {
    event.stopPropagation();
    showConfirmation("Notes", workOrder.notes, [
      {
        color: ThemeColorsEnum.quaternary,
        onClick: () => {},
        title: "OK",
      },
    ]);
  };

  const menuItems = (row) => [
    // Edit
    {
      onClick: () =>
        navigate(AppRoutes.scheduledOrders_edit.replace(":id", row.id), {
          state: { scheduledOrder: row },
        }),
      title: "Edit",
    },
    // Duplicate
    ...(userInfo?.canCreateDeliveryOrders
      ? [
          {
            onClick: () => {
              navigate(AppRoutes.scheduledOrders_create, {
                state: {
                  scheduledOrder: {
                    ...row,
                    id: 0,
                    dueDate: null,
                    scheduleJSON: { ...row.scheduleJSON, startDate: null },
                  },
                },
              });
            },
            title: "Duplicate",
          },
        ]
      : []),
    { type: "divider" },
    // Delete
    {
      className: "text-danger",
      onClick: () => {
        showConfirmation(
          "Confirmation",
          "Do you really want to delete this product?",
          [
            {
              color: ThemeColorsEnum.quaternary,
              title: "Cancel",
            },
            {
              color: ThemeColorsEnum.danger,
              onClick: () => handleDelete(row),
              title: "Delete",
            },
          ]
        );
      },
      title: "Delete",
    },
  ];

  const columns = [
    {
      header: {
        className: "col-menu",
        id: ScheduledOrderColumnsEnum.actionButton,
        name: "id",
      },
      cell: (row) => (
        <DropdownSelect
          color={ThemeColorsEnum.quaternary}
          fixedTitle
          items={menuItems(row)}
          itemLabelProperty="title"
          itemValueProperty="title"
          size="sm"
          style={{ width: "auto" }}
          title={<i className="fa fa-bars" />}
          // title={<MenuIcon />}
        />
      ),
      dataTitle: "Menu",
      className: "nowrap",
    },
    {
      header: {
        id: ScheduledOrderColumnsEnum.customer,
        name: "customer",
        text: "Customer",
        sortable: true,
      },
      cell: (row) => row.customerDisplayName,
      dataTitle: "Customer",
      className: "nowrap",
    },
    {
      header: {
        id: ScheduledOrderColumnsEnum.customerLocation,
        name: "location",
        text: "Location",
        sortable: false,
      },
      cell: (row) => row.customerLocationName,
      dataTitle: "Location",
      className: "nowrap",
    },
    {
      header: {
        id: ScheduledOrderColumnsEnum.notes,
        name: "notes",
        text: "Notes",
        sortable: true,
      },
      cell: (row) =>
        row.notes ? (
          <CircleButton
            color={ThemeColorsEnum.secondary}
            size={30}
            // style={{ backgroundColor: "#eaeaea" }}
            IconComponent={() => (
              <ChatIcon className="cur-pointer" style={{ fontSize: 20 }} />
            )}
            onClick={(event) => {
              handleShowNotes(row, event);
            }}
          />
        ) : null,
      dataTitle: "Notes",
      className: "nowrap",
    },
    // {
    //   header: {
    //     id: ScheduledOrderColumnsEnum.notes,
    //     name: "notes",
    //     text: "Notes",
    //     sortable: true,
    //   },
    //   cell: (row) => row.notes,
    //   dataTitle: "Notes",
    //   className: "nowrap",
    // },
    {
      header: {
        id: ScheduledOrderColumnsEnum.products,
        name: "products",
        text: "Products",
        sortable: true,
      },
      cell: (row) =>
        row?.productsListFormatted?.map((productInfo, index) => (
          <div key={productInfo + index}>{productInfo}</div>
        )),
      dataTitle: "Products",
      className: "nowrap",
    },
    {
      header: {
        id: ScheduledOrderColumnsEnum.createOn,
        name: "createOn",
        text: "Create on",
        sortable: true,
      },
      cell: (row) =>
        Services.utility.dayNameFromIndex(row.scheduleJSON.createOn),
      dataTitle: "Create on",
      className: "nowrap",
    },
    {
      header: {
        id: ScheduledOrderColumnsEnum.dueOn,
        name: "dueOn",
        text: "Due on",
        sortable: true,
      },
      cell: (row) => Services.utility.dayNameFromIndex(row.scheduleJSON.dueOn),
      dataTitle: "Due on",
      className: "nowrap",
    },
    {
      header: {
        id: ScheduledOrderColumnsEnum.interval,
        name: "interval",
        text: "Repeat (weeks)",
        sortable: true,
      },
      cell: (row) => `${row.scheduleJSON.weeksInterval} week(s)`,
      dataTitle: "Repeat (weeks)",
      className: "nowrap",
    },
    {
      header: {
        id: ScheduledOrderColumnsEnum.startDate,
        name: "startDate",
        text: "Start Date",
        sortable: true,
      },
      cell: (row) =>
        Services.utility.formatFullDate(row.scheduleJSON.startDate),
      dataTitle: "Start Date",
      className: "nowrap",
    },
  ];

  return { columns };
};

export default useSchedulesDataTable;
